/**
 * ホーム（05-ui.md §8.1）。
 *
 * 「次にやるべき 1 つ」を最上部に出すのがこの画面の役目。
 * 数字を並べるより、DNS 未設定・容量逼迫・支払い遅延といった
 * **行動が必要な状態**を先に見せる。
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
  Section,
  StatCard,
  StatusBadge,
  UsageBar,
  formatBytes,
  formatDateTime,
  formatCount,
} from '@mail-sv/ui';
import {
  AtSign,
  CreditCard,
  Globe,
  HardDrive,
  HelpCircle,
  Inbox,
  Mail,
  Send,
  Settings2,
} from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useDomains, useOrg } from '../lib/hooks.js';
import { AppLink } from '../lib/nav.js';
import { Async, RefetchingDot } from '../components/async.js';
import { OnboardingChecklist } from '../components/onboarding-checklist.js';
import { formatLimit } from '../lib/format.js';
import { mailEventLabel, severityTone, SEVERITY_LABEL } from '../lib/mail-events.js';
import type { CursorPage, MailEventLogEntry } from '../lib/api-types.js';

export function DashboardPage() {
  const org = useOrg();
  const domains = useDomains();

  const recent = useQuery({
    queryKey: qk.mailLogs({ scope: 'dashboard' }),
    queryFn: () => api.get<CursorPage<MailEventLogEntry>>('/logs/mail?limit=5'),
    staleTime: 30_000,
  });

  const domainList = domains.data?.items ?? [];
  // `dnsStatus` の語彙は API（DB の `dns_status`）と同じ 'unknown'|'ok'|'partial'|'broken'。
  const needsDns = domainList.filter((d) => d.dnsStatus !== 'ok');

  return (
    <>
      <PageHeader
        title="ホーム"
        description="ご契約のメールサービスの状況をまとめて確認できます。"
        actions={<RefetchingDot active={org.isFetching || domains.isFetching} />}
      />

      <div className="space-y-6">
        {/*
         * 初期設定のチェックリスト（6 段）。判定はサーバ（`GET /onboarding`）が
         * 持ち、全段が終わると自分で消える。**ここに条件分岐を書かない**
         * （画面ごとに「出す / 出さない」を書くと必ず食い違う）。
         */}
        <OnboardingChecklist />

        <Async query={org} lines={4}>
          {(data) => (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <StatCard
                label="メールアドレス"
                value={formatCount(data.usage.mailboxes)}
                unit={`/ ${formatLimit(data.plan.limits.mailboxesMax)} 件`}
                icon={AtSign}
                hint="ご契約プランで作成できる上限です"
              />
              <div className="rounded-xl border border-border bg-card p-4 shadow-lg shadow-black/10">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    使用容量
                  </p>
                  <HardDrive className="size-4 text-muted-foreground" aria-hidden />
                </div>
                <p className="mt-2 text-2xl font-semibold tabular">
                  {formatBytes(data.usage.storageBytes)}
                </p>
                <UsageBar
                  className="mt-2"
                  used={data.usage.storageBytes}
                  total={data.plan.limits.storageBytesTotal}
                  formatValue={(n) => formatBytes(n)}
                />
              </div>
              <StatCard
                label="今月の送信数"
                value={formatCount(data.usage.sentThisMonth)}
                unit="通"
                icon={Send}
                hint={`本日 ${formatCount(data.usage.sentToday)} 通 / 上限 ${formatLimit(
                  data.plan.limits.sendPerDayMax,
                )} 通`}
              />
            </div>
          )}
        </Async>

        {needsDns.length > 0 ? (
          <Alert variant="warning">
            <AlertTitle>DNS の設定が完了していないドメインがあります</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>
                DNS の設定が終わるまで、そのドメイン宛のメールは届かず、送信もできません。
                設定は 10 分ほどで終わります。
              </p>
              <div className="flex flex-wrap gap-2">
                {needsDns.map((d) => (
                  <Button key={d.id} asChild variant="outline" size="sm">
                    <AppLink to={`/domains/${d.id}/dns`}>
                      {d.name} の DNS を設定する
                    </AppLink>
                  </Button>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        ) : null}

        <Section title="ドメインの状態" description="ドメインごとの設定状況です。">
          {domainList.length === 0 ? (
            <EmptyState
              icon={Globe}
              title="まずはドメインを登録しましょう"
              description="お持ちのドメイン（example.jp など）を登録すると、そのドメインのメールアドレスを作れます。"
              action={
                <Button asChild>
                  <AppLink to="/domains">ドメインを追加する</AppLink>
                </Button>
              }
            />
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {domainList.map((d) => (
                <Card key={d.id}>
                  <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <div className="min-w-0 space-y-1">
                      <p className="truncate text-base font-semibold">{d.name}</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge status={d.status} size="sm" dot />
                        <Badge variant="muted" size="sm">
                          メールアドレス {d.mailboxCount} 件
                        </Badge>
                      </div>
                    </div>
                    <Button asChild variant={d.dnsStatus === 'ok' ? 'outline' : 'default'} size="sm">
                      <AppLink to={`/domains/${d.id}/dns`}>
                        {d.dnsStatus === 'ok' ? '設定を確認' : 'DNS を設定する'}
                      </AppLink>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </Section>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Section title="よく使う操作">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <QuickAction
                to="/mailboxes"
                icon={AtSign}
                title="メールアドレスを作る"
                description="info@ や support@ などを追加します"
              />
              <QuickAction
                to="/webmail"
                icon={Mail}
                title="Webメールを開く"
                description="ブラウザでメールを読み書きします"
              />
              <QuickAction
                to="/mail-clients"
                icon={Settings2}
                title="メールソフトの設定"
                description="Outlook・iPhone などの設定値を確認します"
              />
              <QuickAction
                to="/logs"
                icon={Inbox}
                title="メールの履歴を見る"
                description="届いた / 送った メールの記録を確認します"
              />
              {/*
               * **使い方ガイドをここに置く。** 「はじめの設定」の段が全部
               * 終わるとチェックリストは消えるので、そのあと何ができるのかを
               * 知る手段が製品の中に無かった（ヘッダの「ヘルプ」は
               * `VITE_SUPPORT_URL` が設定されているときだけ出る外部リンク）。
               */}
              <QuickAction
                to="/help"
                icon={HelpCircle}
                title="使い方ガイドを見る"
                description="設定の手順と、届かないときの調べ方"
              />
              <QuickAction
                to="/billing"
                icon={CreditCard}
                title="料金プラン・請求"
                description="お支払い方法の登録と請求書の確認"
              />
            </div>
          </Section>

          <Section
            title="メールまわりの最近のできごと"
            description="サーバーが検知した最新 5 件です。"
          >
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">最近のできごと</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {recent.isPending ? (
                  <p className="text-sm text-muted-foreground">読み込んでいます…</p>
                ) : (recent.data?.items.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    記録はありません。問題なく動作しています。
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {(recent.data?.items ?? []).map((log) => (
                      <li key={log.id} className="flex items-center justify-between gap-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm">{mailEventLabel(log.kind)}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {formatDateTime(log.occurredAt)}
                            {log.sourceIp ? ` / ${log.sourceIp}` : ''}
                          </p>
                        </div>
                        <StatusBadge
                          status={severityTone(log.severity)}
                          label={SEVERITY_LABEL[log.severity]}
                          size="sm"
                        />
                      </li>
                    ))}
                  </ul>
                )}
                <Button asChild variant="ghost" size="sm" className="w-full">
                  <AppLink to="/logs">すべての履歴を見る</AppLink>
                </Button>
              </CardContent>
            </Card>
          </Section>
        </div>
      </div>
    </>
  );
}

function QuickAction({
  to,
  icon: Icon,
  title,
  description,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <AppLink
      to={to}
      className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-hover"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
        <Icon className="size-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-foreground">{title}</span>
        <span className="block text-xs text-muted-foreground">{description}</span>
      </span>
    </AppLink>
  );
}
